/**
 * Dark Google Maps style, applied inline.
 *
 * Inline `styles` rather than a Cloud-console style deliberately: a `mapId` and a
 * `styles` array are mutually exclusive, and requiring a console-configured style would
 * mean the app cannot render until someone has clicked through Google Cloud. This way an
 * API key is the only prerequisite.
 *
 * ── Why the land colour is not a free choice ──────────────────────────────────
 *
 * LAND must stay close to #14141a. The six-step orange demand ramp in theme.ts was run
 * through a contrast validator against exactly that surface — monotone lightness, every
 * adjacent gap ≥ 0.06, the dark end at 2.35:1. Lighten the land materially and those
 * results no longer describe what is on screen; the ramp would have to be re-validated
 * against the new surface. Change it only together with the ramp.
 *
 * ── Why the labels are pushed so hard ─────────────────────────────────────────
 *
 * The demand hexes are drawn *over* this basemap, labels included — deck.gl's Google
 * overlay cannot interleave beneath them the way the previous MapLibre basemap could.
 * So street names have to survive being covered by a translucent orange fill. They get
 * a brighter fill and a heavier dark halo than a normal dark theme would use. It looks
 * slightly over-contrasted with no overlay on top; with the overlay it is about right.
 */

/** The validated surface. Keep in step with COLORS.surface and the ramp validation. */
const LAND = '#14141a';

const WATER = '#0c1826';
const PARK = '#15241a';

const ROAD_LOCAL = '#2b2b35';
const ROAD_ARTERIAL = '#3a3a46';
const ROAD_HIGHWAY = '#4a4a57';

const LABEL_FILL = '#e6e6ee';
const LABEL_HALO = '#0b0b0f';
const LABEL_FILL_DIM = '#9a9aa6';

export const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  // Base.
  { elementType: 'geometry', stylers: [{ color: LAND }] },
  { elementType: 'labels.text.fill', stylers: [{ color: LABEL_FILL }] },
  // A heavy halo is what lets a name stay readable through the demand overlay.
  { elementType: 'labels.text.stroke', stylers: [{ color: LABEL_HALO }, { weight: 4 }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },

  // Administrative boundaries: present but recessive.
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#2e2e39' }],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels.text.fill',
    // Neighbourhood names are the most useful labels on this map — Florentin, Neve
    // Tzedek, Kerem HaTeimanim are how the job is actually navigated.
    stylers: [{ color: LABEL_FILL }],
  },

  // Buildings — requested, and genuinely useful for orientation at close zoom.
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry',
    stylers: [{ color: '#1c1c24' }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#191920' }],
  },

  // Parks and green space: Park HaYarkon, Meir Park, the Charles Clore lawn.
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: PARK }] },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7f9c86' }],
  },

  // Business POIs off. Their labels would compete with both the demand overlay and the
  // named landmarks already listed in the panel, and three competing sources of venue
  // names on one small screen is worse than one good one.
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: LABEL_FILL_DIM }],
  },

  // Roads, in three weights so the street hierarchy reads at a glance.
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: ROAD_LOCAL }] },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: LABEL_FILL }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: ROAD_ARTERIAL }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: ROAD_HIGHWAY }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry',
    // The Ayalon. Worth seeing clearly: it is the hard barrier the routing model already
    // knows about, and the reason cells across it are far apart in minutes.
    stylers: [{ color: '#55555f' }],
  },

  // Transit: keep rail geometry for orientation, drop the station clutter.
  { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#22222b' }],
  },

  // Water: the Mediterranean and the Yarkon. Distinctly blue so the edges of the working
  // area read as sea and river rather than as void.
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: WATER }] },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4a6b8a' }],
  },
];

/** Exported for the test that guards the ramp-validation invariant. */
export const VALIDATED_LAND_COLOR = LAND;
